import React, { useState, useEffect } from 'react';
import { getOCELMetadata } from '../../../services/apiClient';
import { berlinLocalToISOString } from '../../../utils/berlinDate';

// Splits a stored value (bare 'YYYY-MM-DD' or a full ISO-with-offset string)
// into separate date/time parts for the native date/time inputs.
const splitDateTime = (raw) => {
  if (!raw) return { date: '', time: '00:00:00' };
  if (!raw.includes('T')) return { date: raw, time: '00:00:00' };
  const [datePart, rest] = raw.split('T');
  return { date: datePart, time: rest.slice(0, 8) };
};

// Recomposes a date + time-of-day pair into a DST-aware Europe/Berlin ISO string.
const composeBerlinISO = (dateStr, timeStr) => {
  if (!dateStr) return null;
  const [y, m, d] = dateStr.split('-').map(Number);
  const [hh = 0, mm = 0, ss = 0] = (timeStr || '00:00:00').split(':').map(Number);
  return berlinLocalToISOString(y, m, d, hh, mm, ss);
};

/**
 * DateRangeFilterControl - Date range picker with enable toggle
 *
 * Allows users to filter events by date range.
 * Fetches dataset bounds from OCEL metadata to provide min/max constraints.
 *
 * @param {object} value - Current date range { startDate, endDate, enabled }
 * @param {Function} onChange - Callback when range changes
 * @param {string} activeFile - Current active file for fetching metadata
 * @param {boolean} disabled - Whether control is disabled
 * @param {boolean} showTime - Whether to also show paired time-of-day inputs (default false, keeps existing day-granularity behavior for per-tile local filters)
 */
const DateRangeFilterControl = ({ value = { startDate: null, endDate: null, enabled: false }, onChange, activeFile, disabled = false, showTime = false }) => {
  const [datasetBounds, setDatasetBounds] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch dataset date bounds from metadata
  useEffect(() => {
    const fetchDateBounds = async () => {
      if (!activeFile) return;

      setLoading(true);
      try {
        const metadata = await getOCELMetadata(activeFile);
        console.log('📊 DateRangeFilterControl - Fetched metadata:', metadata);

        if (metadata?.date_range) {
          setDatasetBounds({
            startDate: metadata.date_range.start ? metadata.date_range.start.split('T')[0] : null,
            endDate: metadata.date_range.end ? metadata.date_range.end.split('T')[0] : null
          });
          console.log('✅ DateRangeFilterControl - Loaded date bounds:', metadata.date_range);
        }
      } catch (err) {
        console.error('❌ DateRangeFilterControl - Failed to fetch date bounds:', err);
        setDatasetBounds(null);
      } finally {
        setLoading(false);
      }
    };

    fetchDateBounds();
  }, [activeFile]);

  const handleEnabledChange = (enabled) => {
    if (disabled) return;
    onChange({
      ...value,
      enabled
    });
  };

  const handleStartDateChange = (dateStr) => {
    if (disabled) return;
    const { time } = splitDateTime(value.startDate);
    onChange({
      ...value,
      startDate: showTime ? composeBerlinISO(dateStr, time) : dateStr,
      enabled: true, // Auto-enable when user selects a date
      presetKey: undefined // manual edit overrides any preset previously applied via DateRangePresets
    });
  };

  const handleEndDateChange = (dateStr) => {
    if (disabled) return;
    const { time } = splitDateTime(value.endDate);
    onChange({
      ...value,
      endDate: showTime ? composeBerlinISO(dateStr, time) : dateStr,
      enabled: true, // Auto-enable when user selects a date
      presetKey: undefined // manual edit overrides any preset previously applied via DateRangePresets
    });
  };

  const handleStartTimeChange = (timeStr) => {
    if (disabled) return;
    const { date } = splitDateTime(value.startDate);
    if (!date) return; // need a date first
    onChange({ ...value, startDate: composeBerlinISO(date, timeStr), enabled: true, presetKey: undefined });
  };

  const handleEndTimeChange = (timeStr) => {
    if (disabled) return;
    const { date } = splitDateTime(value.endDate);
    if (!date) return; // need a date first
    onChange({ ...value, endDate: composeBerlinISO(date, timeStr), enabled: true, presetKey: undefined });
  };

  const handleClear = () => {
    if (disabled) return;
    onChange({
      startDate: null,
      endDate: null,
      enabled: false
    });
  };

  if (loading) {
    return (
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Date Range</label>
        <div className="text-sm text-gray-500 italic">Loading date range...</div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          Date Range
        </label>
        <label className="flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={value.enabled}
            onChange={(e) => handleEnabledChange(e.target.checked)}
            disabled={disabled}
            className="mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <span className="text-sm text-gray-600">Enabled</span>
        </label>
      </div>

      {datasetBounds && (
        <div className="text-xs text-gray-500 italic">
          Dataset range: {datasetBounds.startDate} to {datasetBounds.endDate}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Start Date</label>
          <input
            type="date"
            value={splitDateTime(value.startDate).date}
            onChange={(e) => handleStartDateChange(e.target.value)}
            disabled={disabled}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
          {showTime && (
            <input
              type="time"
              step="1"
              value={splitDateTime(value.startDate).time}
              onChange={(e) => handleStartTimeChange(e.target.value)}
              disabled={disabled || !splitDateTime(value.startDate).date}
              className="w-full mt-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">End Date</label>
          <input
            type="date"
            value={splitDateTime(value.endDate).date}
            onChange={(e) => handleEndDateChange(e.target.value)}
            disabled={disabled}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
          {showTime && (
            <input
              type="time"
              step="1"
              value={splitDateTime(value.endDate).time}
              onChange={(e) => handleEndTimeChange(e.target.value)}
              disabled={disabled || !splitDateTime(value.endDate).date}
              className="w-full mt-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
          )}
        </div>
      </div>

      {(value.startDate || value.endDate) && (
        <button
          type="button"
          onClick={handleClear}
          disabled={disabled}
          className="text-xs text-blue-600 hover:text-blue-800 disabled:text-gray-400 disabled:cursor-not-allowed"
        >
          Clear Dates
        </button>
      )}

      {!value.enabled && (value.startDate || value.endDate) && (
        <div className="text-xs text-amber-600 italic">
          Date range is set but not enabled. Check "Enabled" to apply the filter.
        </div>
      )}

      {value.enabled && !value.startDate && !value.endDate && (
        <div className="text-xs text-gray-500 italic">
          No dates selected (all dates will be included)
        </div>
      )}
    </div>
  );
};

export default DateRangeFilterControl;
